// Staff-only prelead management actions (Area D) — shared by the Preleads
// dashboard rows and the read-only detail drawer's header. Disqualify + Archive
// gate on LeadGen.Manage; Delete + Restore are ADMIN-only. Each action self-gates
// on the caller's permissions, so a lead handler who lacks them sees no menu. The
// generator portal never mounts this.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, OverflowMenu, Textarea, useToast } from "@/design-system/components/ui";
import type { OverflowMenuItem } from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import type { PreleadStatus } from "@/api/leadGen/preleads";
import {
  useArchivePrelead,
  useDeletePrelead,
  useDisqualifyPrelead,
  useRestorePrelead,
  useUnarchivePrelead,
} from "@/hooks/leadGen/usePreleads";

export interface PreleadActionTarget {
  id: string;
  status: PreleadStatus;
  isArchived: boolean;
  displayName: string;
}

export function PreleadStaffActions({
  prelead,
  deletedView = false,
  onDone,
}: {
  prelead: PreleadActionTarget;
  /** In the ADMIN "Deleted" list view a row is soft-deleted → offer Restore. */
  deletedView?: boolean;
  /** Called after a successful mutation (the drawer wires this to onClose). */
  onDone?: () => void;
}) {
  const { hasPermission, hasRoleCode } = useAuth();
  const canManage = hasPermission("LeadGen.Manage");
  const isAdmin = hasRoleCode("ADMIN");
  const toast = useToast();

  const archive = useArchivePrelead();
  const unarchive = useUnarchivePrelead();
  const del = useDeletePrelead();
  const restore = useRestorePrelead();
  const [disqualifyOpen, setDisqualifyOpen] = useState(false);

  const onError = (err: unknown) => toast.error(extractErrorMessage(err));
  const done = (msg: string) => {
    toast.success(msg);
    onDone?.();
  };

  const items: OverflowMenuItem[] = [];
  if (deletedView) {
    if (isAdmin) {
      items.push({
        label: "Restore",
        onSelect: () =>
          restore.mutate(prelead.id, { onSuccess: () => done("Prelead restored"), onError }),
      });
    }
  } else {
    if (canManage && prelead.status !== "Disqualified") {
      items.push({ label: "Disqualify…", onSelect: () => setDisqualifyOpen(true) });
    }
    if (canManage) {
      items.push({
        label: prelead.isArchived ? "Unarchive" : "Archive",
        destructive: !prelead.isArchived,
        confirm: prelead.isArchived
          ? undefined
          : "Archive this prelead? Any still-pending escalation credit is voided.",
        onSelect: () =>
          (prelead.isArchived ? unarchive : archive).mutate(prelead.id, {
            onSuccess: () => done(prelead.isArchived ? "Prelead unarchived" : "Prelead archived"),
            onError,
          }),
      });
    }
    if (isAdmin) {
      items.push({
        label: "Delete",
        destructive: true,
        confirm:
          "Soft-delete this prelead? It is hidden but retained (earnings + history kept) and an admin can restore it. Any still-pending credit is voided.",
        onSelect: () =>
          del.mutate(prelead.id, { onSuccess: () => done("Prelead deleted"), onError }),
      });
    }
  }

  if (items.length === 0) return null;

  return (
    <>
      <OverflowMenu items={items} label={`Actions for ${prelead.displayName}`} />
      {disqualifyOpen && (
        <DisqualifyPreleadModal
          prelead={prelead}
          onClose={() => setDisqualifyOpen(false)}
          onDone={onDone}
        />
      )}
    </>
  );
}

function DisqualifyPreleadModal({
  prelead,
  onClose,
  onDone,
}: {
  prelead: PreleadActionTarget;
  onClose: () => void;
  onDone?: () => void;
}) {
  const toast = useToast();
  const disqualify = useDisqualifyPrelead();
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const canSubmit = reason.trim().length > 0 && !disqualify.isPending;

  function submit() {
    disqualify.mutate(
      { id: prelead.id, reason: reason.trim(), notes: notes.trim() || null },
      {
        onSuccess: () => {
          toast.success("Prelead disqualified");
          onClose();
          onDone?.();
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Disqualify prelead"
      description="Flips the prelead to Disqualified and voids any still-pending escalation credit. The generator sees the status change + your reason on drill-in."
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={disqualify.isPending}
            disabled={!canSubmit}
            onClick={submit}
          >
            Disqualify
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <label className="flex flex-col gap-space-1">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Reason (required)</span>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Why is this prelead being disqualified?"
            aria-label="Disqualification reason"
          />
        </label>
        <label className="flex flex-col gap-space-1">
          <span className="text-caption uppercase tracking-wide text-gray-mid">Notes (optional)</span>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Additional context for the generator…"
            aria-label="Disqualification notes"
          />
        </label>
        {disqualify.isError && (
          <p className="text-caption text-status-error m-0" role="alert">
            {extractErrorMessage(disqualify.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
